import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import useSEO from '../hooks/useSEO';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } })
};

export default function SEOPageLayout({ badge, title, subtitle, children, metaTitle, metaDescription, metaKeywords, canonicalPath }) {
  useSEO({
    title: `${metaTitle || title} | Krieger Real Estate`,
    description: metaDescription || subtitle || '',
    keywords: metaKeywords,
    canonical: canonicalPath ? `https://joinkrieger.com${canonicalPath}` : undefined,
  });

  return (
    <div className="bg-[#FAFBFC] min-h-screen pt-20">
      <section className="py-16 md:py-24 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
            {badge && <motion.span variants={fadeUp} className="text-[10px] tracking-[0.3em] uppercase text-[#002FA7] font-semibold bg-[#EFF3FF] px-4 py-1.5 rounded-full inline-block mb-5">{badge}</motion.span>}
            <motion.h1 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-['Cabinet_Grotesk'] font-bold tracking-tight leading-tight mb-5 text-slate-900">{title}</motion.h1>
            {subtitle && <motion.p variants={fadeUp} className="text-base md:text-lg text-slate-500 leading-relaxed max-w-2xl">{subtitle}</motion.p>}
          </motion.div>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
        {children}
      </div>
      <section className="py-16 border-t border-slate-200 bg-[#F4F6F8]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-['Cabinet_Grotesk'] font-bold mb-4 text-slate-900">Ready to Make the Switch?</h2>
          <p className="text-sm text-slate-500 mb-8 max-w-md mx-auto">Join the brokerage built for agents who want to keep more of what they earn.</p>
          <Link to="/apply" className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-8 py-3 rounded-full font-medium text-sm hover:bg-[#0040D6] transition-colors shadow-lg shadow-blue-900/20">
            Apply to Join <ArrowRight size={16} />
          </Link>
        </div>
      </section>
      <footer className="border-t border-slate-200 py-10 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <img src="/krieger-logo.jpg" alt="Krieger" className="h-6 w-6 object-contain" />
            <span className="font-['Cabinet_Grotesk'] text-lg font-bold tracking-[0.15em] text-slate-900">KRIEGER</span>
          </div>
          <div className="flex flex-wrap gap-5 text-xs text-slate-400">
            <Link to="/" className="hover:text-slate-700">Home</Link>
            <Link to="/commission-plans" className="hover:text-slate-700">Commission Plans</Link>
            <Link to="/why-join-krieger" className="hover:text-slate-700">Why Krieger</Link>
            <Link to="/agent-income-calculator" className="hover:text-slate-700">Calculator</Link>
            <Link to="/apply" className="hover:text-slate-700">Apply</Link>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400"><MapPin size={12} /> NY &middot; PA &middot; FL</div>
        </div>
      </footer>
    </div>
  );
}
